/**
 * DIRECT WORK (MODES.md §6, M6) — the room's direct runs (Context + agent), kept OUT of the network-run reducer.
 *
 * The room reducer models ONE live network run (one currentRunId, one live turn). A direct run is parallel by
 * design, so its envelopes — every one carries its ``dr-…`` run id (or is the ``direct`` prompt) — are split off
 * BEFORE that reducer (so a direct ask never resets a live network run's view) and each direct run is reduced ON
 * ITS OWN with the same reducer: its own block (question → live builder → answer) and its own dock row.
 */

const DIRECT_PREFIX = 'dr-';

function typeOf(e) {
  return String((e && (e.eventType ?? e.type)) || '');
}

/** The direct run an envelope belongs to ('' = not a direct run). Pure. */
export function directRunIdOf(e) {
  if (!e || typeof e !== 'object') return '';
  const p = (e.payload && typeof e.payload === 'object') ? e.payload : {};
  const ids = [p.runId, p.taskId, e.executionId, e.correlationId, p.executionId];
  for (const v of ids) {
    const s = String(v || '').trim();
    if (s.startsWith(DIRECT_PREFIX)) return s;
  }
  return '';
}

/** Split the room buffer: ``main`` (everything a network run owns — order kept) and ``byRun`` (each direct run's
 *  envelopes, in order). Returns the SAME ``main`` array when there is no direct envelope (no re-render churn). */
export function splitDirectEnvelopes(envelopes = []) {
  const byRun = new Map();
  let any = false;
  const main = [];
  for (const e of envelopes || []) {
    const rid = directRunIdOf(e);
    if (rid) {
      any = true;
      if (!byRun.has(rid)) byRun.set(rid, []);
      byRun.get(rid).push(e);
    } else {
      main.push(e);
    }
  }
  return { main: any ? main : (envelopes || []), byRun };
}

/** One direct run's view, from its own envelopes reduced on their own (``reduce`` = the room reducer). Pure
 *  apart from ``reduce``. ``{runId, agentId, agentName, question, status, answer, error, startedAt, endedAt,
 *  turns, rosterRows}`` — status ``starting|running|done|failed|stopped``. */
export function directRunView(runId, envs, reduce) {
  let agentId = '';
  let agentName = '';
  let question = '';
  let startedAt = '';
  let endedAt = '';
  let status = 'starting';
  let answer = '';
  let error = '';
  for (const e of envs || []) {
    const p = (e.payload && typeof e.payload === 'object') ? e.payload : {};
    const t = typeOf(e);
    const ts = String(e.timestamp || e.ts || '');
    if (t === 'USER_PROMPT') {
      question = String(p.prompt || p.text || '');
      startedAt = startedAt || ts;
    } else if (t === 'BUILDER_PHASE') {
      agentId = agentId || String(p.agentId || '');
      if (status === 'starting') status = 'running';
    } else if (t === 'LOG_LINE' && status === 'starting') {
      status = 'running';
    } else if (t === 'AGENT_FINAL_RESPONSE') {
      agentId = agentId || String(p.agentName || '');
      agentName = agentName || String(p.agentDisplayName || '');
      answer = String(p.text || '');
      if (p.status) status = String(p.status);
    } else if (t === 'DIRECT_RUN_FINISHED') {
      agentId = agentId || String(p.agentId || '');
      agentName = String(p.agentName || agentName || '');
      status = String(p.status || status);
      error = String(p.error || '');
      endedAt = ts;
    }
  }
  // "Direct · Architect: …" is how the Executions row + prompt label the question — show the request itself
  const m = /^Direct · [^:]+:\s*/.exec(question);
  if (m) question = question.slice(m[0].length);
  const name = agentName || agentId;
  let turns = [];
  let rosterRows = [];
  if (typeof reduce === 'function') {
    const r = reduce(envs || []);
    turns = (r && Array.isArray(r.turnGroups)) ? r.turnGroups : [];
    rosterRows = ((r && Array.isArray(r.agentRoster)) ? r.agentRoster : [])
      .filter((row) => row && row.agentId)
      .map((row) => ({ ...row, agentId: `${row.agentId}@${runId}`, label: `Direct · ${name || row.label || row.agentId}`,
        runId: row.runId || runId, isDirect: true }));
  }
  return { runId, agentId, agentName: name, question, status, answer, error, startedAt, endedAt, turns, rosterRows };
}

/** Every direct run in the buffer, oldest first. Pure apart from ``reduce``. */
export function directRunViews(byRun, reduce) {
  const out = [];
  for (const [rid, envs] of (byRun || new Map()).entries()) out.push(directRunView(rid, envs, reduce));
  return out.sort((a, b) => (a.runId < b.runId ? -1 : a.runId > b.runId ? 1 : 0));   // ids are time-sortable
}

export const DIRECT_OPEN = new Set(['starting', 'running']);
